import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence } from "framer-motion";
import { Button } from "../ui/Button";
import { CreditCard, DollarSign, Loader2, CheckCircle, AlertCircle, Building2, Edit2, Trash2, Plus } from "lucide-react";
import StripeConnectSetup from "../ui/StripeConnectSetup";
import PayPalPaymentMethodForm from "./PayPalPaymentMethodForm";
import BankAccountForm from "./BankAccountForm";
import UpdatePayPalEmailForm from "./UpdatePayPalEmailForm";
import { usePaymentMethods } from "../../contexts/PaymentMethodsContext";

interface WithdrawalMethodsSetupProps {
  onComplete?: () => void;
  onError?: (error: string) => void;
  className?: string;
}

const WithdrawalMethodsSetup: React.FC<WithdrawalMethodsSetupProps> = ({
  onComplete,
  onError,
  className = "",
}) => {
  const { t } = useTranslation();
  const { paymentMethods, loading: paymentMethodsLoading, deletePaymentMethod } = usePaymentMethods();
  const [showAddPayPal, setShowAddPayPal] = useState(false);
  const [showAddBankAccount, setShowAddBankAccount] = useState(false);
  const [showUpdatePayPal, setShowUpdatePayPal] = useState(false);
  const [showUpdateBankAccount, setShowUpdateBankAccount] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState<any | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [deletingMethod, setDeletingMethod] = useState<string | null>(null);

  // Get payment methods by type
  const paypalMethods = paymentMethods.filter(method => method.type === "paypal");
  const bankMethods = paymentMethods.filter(method => 
    method.type === "bank_account" || 
    method.type === "us_bank_account" || 
    method.type === "sepa_bank_account"
  );
  const hasPayPal = paypalMethods.length > 0;
  const hasBankAccount = bankMethods.length > 0;

  // Handle delete method
  const handleDelete = async (methodId: string, methodType: string) => {
    const confirmDelete = window.confirm(
      methodType === "paypal" 
        ? t("withdrawalMethodsSetup.confirmDeletePaypal")
        : t("withdrawalMethodsSetup.confirmDeleteBank")
    );
    
    if (!confirmDelete) return;

    try {
      setDeletingMethod(methodId);
      await deletePaymentMethod(methodId);
    } catch (err: any) {
      alert(err?.response?.data?.message || t("withdrawalMethodsSetup.deletePaymentMethodFailed"));
    } finally {
      setDeletingMethod(null);
    }
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div>
        <h3 className="text-lg font-medium text-gray-900">{t("withdrawalMethodsSetup.title")}</h3>
        <p className="text-sm text-gray-600 mt-1">
          {t("withdrawalMethodsSetup.subtitle")}
        </p>
      </div>

      {/* Stripe Connect Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-start gap-4 mb-4">
          <div className="p-2 bg-blue-100 rounded-lg">
            <CreditCard className="h-6 w-6 text-blue-600" />
          </div>
          <div className="flex-1">
            <h4 className="text-base font-semibold text-gray-900">{t("withdrawalMethodsSetup.stripeConnectTitle")}</h4>
            <p className="text-sm text-gray-600 mt-1">
              {t("withdrawalMethodsSetup.stripeConnectDesc")}
            </p>
          </div>
        </div>

        <StripeConnectSetup
          onComplete={() => {
            console.log("Stripe Connect setup completed successfully");
            if (onComplete) {
              onComplete();
            }
          }}
          onError={(error) => {
            console.error("Stripe Connect setup error:", error);
            if (onError) {
              onError(error);
            }
          }}
        />
      </div>

      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-200"></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-2 bg-gray-50 text-gray-500">{t("withdrawalMethodsSetup.or")}</span>
        </div>
      </div>

      {/* PayPal Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-start gap-4 mb-4">
          <div className="p-2 bg-blue-100 rounded-lg">
            <DollarSign className="h-6 w-6 text-blue-600" />
          </div>
          <div className="flex-1">
            <h4 className="text-base font-semibold text-gray-900">{t("withdrawalMethodsSetup.paypalAccountsTitle")}</h4>
            <p className="text-sm text-gray-600 mt-1">
              {t("withdrawalMethodsSetup.paypalAccountsDesc")}
            </p>
          </div>
        </div>

        {paymentMethodsLoading ? (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>{t("withdrawalMethodsSetup.loadingPaypalAccounts")}</span>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Existing PayPal Methods */}
            {paypalMethods.map(method => (
              <div key={method.id} className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1">
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <h5 className="text-sm font-medium text-green-800">{t("withdrawalMethodsSetup.paypalConnected")}</h5>
                      <p className="text-sm text-green-700 mt-1 truncate">
                        {method.paypalEmail}
                      </p>
                      <p className="text-xs text-green-600 mt-1">
                        {t("withdrawalMethodsSetup.addedOn", { date: new Date(method.createdAt).toLocaleDateString() })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        setSelectedMethod(method);
                        setShowUpdatePayPal(true);
                      }}
                      className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                      title={t("withdrawalMethodsSetup.updatePaypalEmail")}
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(method.id, "paypal")}
                      disabled={deletingMethod === method.id}
                      className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors disabled:opacity-50"
                      title={t("withdrawalMethodsSetup.removePaypalAccount")}
                    >
                      {deletingMethod === method.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {/* Add PayPal Button */}
            {!hasPayPal && (
              <Button
                onClick={() => setShowAddPayPal(true)}
                disabled={actionLoading}
                className="w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                variant="outline"
              >
                {actionLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t("withdrawalMethodsSetup.processing")}
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4" />
                    {t("withdrawalMethodsSetup.addPaypalAccount")}
                  </>
                )}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-200"></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-2 bg-gray-50 text-gray-500">{t("withdrawalMethodsSetup.or")}</span>
        </div>
      </div>

      {/* Bank Account Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-start gap-4 mb-4">
          <div className="p-2 bg-green-100 rounded-lg">
            <Building2 className="h-6 w-6 text-green-600" />
          </div>
          <div className="flex-1">
            <h4 className="text-base font-semibold text-gray-900">{t("withdrawalMethodsSetup.bankAccountsTitle")}</h4>
            <p className="text-sm text-gray-600 mt-1">
              {t("withdrawalMethodsSetup.bankAccountsDesc")}
            </p>
          </div>
        </div>

        {paymentMethodsLoading ? (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>{t("withdrawalMethodsSetup.loadingBankAccounts")}</span>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Existing Bank Methods */}
            {bankMethods.map(method => (
              <div key={method.id} className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1">
                    <Building2 className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h5 className="text-sm font-medium text-blue-800">{method.bankName}</h5>
                      <p className="text-sm text-blue-700 mt-1">
                        {method.accountHolderName}
                      </p>
                      <p className="text-xs text-blue-600 mt-1">
                        {method.type === "sepa_bank_account" 
                          ? `${t("withdrawalMethodsSetup.iban")}: ****${method.iban?.slice(-4)}`
                          : `${t("withdrawalMethodsSetup.accountEndingIn")} ****${method.last4 || method.accountNumber?.slice(-4)}`
                        }
                      </p>
                      <p className="text-xs text-blue-600">
                        {method.type === "sepa_bank_account" ? t("withdrawalMethodsSetup.sepaTransfer") : t("withdrawalMethodsSetup.internationalTransfer")} • 
                        {t("withdrawalMethodsSetup.addedOn", { date: new Date(method.createdAt).toLocaleDateString() })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        setSelectedMethod(method);
                        setShowUpdateBankAccount(true);
                      }}
                      className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                      title={t("withdrawalMethodsSetup.editBankAccount")}
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(method.id, "bank_account")}
                      disabled={deletingMethod === method.id}
                      className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors disabled:opacity-50"
                      title={t("withdrawalMethodsSetup.removeBankAccount")}
                    >
                      {deletingMethod === method.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {/* Add Bank Account Button - Only show if no bank account exists */}
            {!hasBankAccount && (
              <Button
                onClick={() => setShowAddBankAccount(true)}
                disabled={actionLoading}
                className="w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                variant="outline"
              >
                {actionLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t("withdrawalMethodsSetup.processing")}
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4" />
                    {t("withdrawalMethodsSetup.addBankAccount")}
                  </>
                )}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Information Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <svg className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div>
            <h4 className="text-sm font-medium text-blue-800">{t("withdrawalMethodsSetup.importantInformation")}</h4>
            <ul className="text-sm text-blue-700 mt-2 space-y-1 list-disc list-inside">
              <li>{t("withdrawalMethodsSetup.stripeConnectInfo")}</li>
              <li>{t("withdrawalMethodsSetup.paypalInfo")}</li>
              <li>{t("withdrawalMethodsSetup.bankAccountInfo")}</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Modals */}
      <AnimatePresence>
        {showAddPayPal && (
          <PayPalPaymentMethodForm 
            onClose={() => {
              setShowAddPayPal(false);
              setActionLoading(false);
            }}
            onSuccess={() => {
              setActionLoading(true);
              setShowAddPayPal(false);
              setTimeout(() => {
                setActionLoading(false);
                if (onComplete) {
                  onComplete();
                }
              }, 1000);
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showAddBankAccount && (
          <BankAccountForm 
            onClose={() => {
              setShowAddBankAccount(false);
              setActionLoading(false);
            }}
            onSuccess={() => {
              setActionLoading(true);
              setShowAddBankAccount(false);
              setTimeout(() => {
                setActionLoading(false);
                if (onComplete) {
                  onComplete();
                }
              }, 1000);
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showUpdateBankAccount && selectedMethod && (
          <BankAccountForm 
            existingMethod={selectedMethod}
            onClose={() => {
              setShowUpdateBankAccount(false);
              setSelectedMethod(null);
              setActionLoading(false);
            }}
            onSuccess={() => {
              setActionLoading(true);
              setShowUpdateBankAccount(false);
              setSelectedMethod(null);
              setTimeout(() => {
                setActionLoading(false);
                if (onComplete) {
                  onComplete();
                }
              }, 1000);
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showUpdatePayPal && selectedMethod && (
          <UpdatePayPalEmailForm 
            existingMethod={selectedMethod}
            onClose={() => {
              setShowUpdatePayPal(false);
              setSelectedMethod(null);
              setActionLoading(false);
            }}
            onSuccess={() => {
              setActionLoading(true);
              setShowUpdatePayPal(false);
              setSelectedMethod(null);
              setTimeout(() => {
                setActionLoading(false);
                if (onComplete) {
                  onComplete();
                }
              }, 1000);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default WithdrawalMethodsSetup;

